import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AdvancedSearch.css';
import CoverImage from './CoverImage';
import {
  addToReadingList,
  clearReadingList,
  downloadTextFile,
  generateBibtex,
  generateRis,
  isInReadingList,
  loadReadingList,
  removeFromReadingList,
} from '../services/readingList';
import { trackEvent } from '../services/analytics';

const ITEM_TYPE_LABELS = {
  AN: 'Author Nook',
  BIOGRAPHIE: 'Autobiographies / Biographies',
  BK: 'Books',
  BKC: 'Books (Customized)',
  BV: 'Bound Volumes / Journals',
  C: "Children's Collection",
  CD: 'Non-Book Materials (CD/DVD/Maps)',
  CM: 'Cartographic Material',
  CP: 'Conference / Proceedings',
  CPDA: 'Longterm Books',
  CR: 'Course Reserve',
  DEB: 'Subscription e-Book',
  DT: 'Demanded Titles',
  E: 'Competitive Exam Collection',
  EB: 'e-Book',
  EBK: 'e-Books',
  FB: 'IITH Faculty Authored Books',
  FC: 'Fiction Collection',
  HF01: 'Hall of Fame',
  JPC: 'Japanese Books Collection',
  'LOST BOOK': 'Lost Book',
  MIT: 'MIT – Movers, Innovators, Thinkers',
  MOOC: 'Massive Open Online Courses',
  PR: 'Project Report',
  QRS: 'Quick Reference Section',
  RAJBHASHA: 'Rajbhasha Hindi Books',
  RC: 'Reference Collection',
  TB: 'Textbook',
  TELGU: 'Telugu Book Collection',
  TH: 'Thesis',
  WC: 'Wheel Chair',
  X1: 'Course Material',
  XM: 'Loose-leaf / Xerox Material',
};

const itemTypeLabel = (code) => ITEM_TYPE_LABELS[code] || code;

const ADVANCED_SEARCH_STORAGE_KEY = 'advancedSearchStateV2';
const ADVANCED_RECENT_SEARCHES_KEY = 'opac_advanced_recent_searches_v1';
const MAX_TERMS = 8;

const fieldToSuggestType = (field = '') => {
  if (field === 'accessionNumber') {
    return 'all';
  }
  if (['title', 'author', 'subject', 'isbn', 'callnumber'].includes(field)) {
    return field;
  }
  return 'all';
};

const tokenizeForHighlight = (value = '') => String(value)
  .toLowerCase()
  .split(/[^a-z0-9]+/)
  .map((token) => token.trim())
  .filter((token) => token.length >= 2)
  .slice(0, 8);

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ── Nested boolean expression tree helpers ──────────────────────────────────
// A node is either:
//   { id, type: 'term', field, value, operator }
//   { id, type: 'group', operator, children: [node, ...] }
// `operator` on a node is the operator joining it to the *previous sibling* in its parent's
// children array; it's ignored on the first child of any group. This shape is sent to the
// backend as-is (see /api/search/advanced's `expression` handling in server.js).
let nodeIdSeq = 0;
const nextNodeId = () => `n${Date.now().toString(36)}${nodeIdSeq++}`;

const makeTerm = (field = 'title', value = '', operator = 'AND') => ({
  id: nextNodeId(), type: 'term', field, value, operator,
});

const makeGroup = (children, operator = 'AND') => ({
  id: nextNodeId(), type: 'group', operator, children: children || [makeTerm()],
});

const countTerms = (node) => (
  node.type === 'term' ? 1 : node.children.reduce((sum, child) => sum + countTerms(child), 0)
);

const flattenTerms = (node, acc = []) => {
  if (node.type === 'term') {
    if (String(node.value || '').trim()) {
      acc.push({ field: node.field, value: node.value.trim() });
    }
    return acc;
  }
  node.children.forEach((child) => flattenTerms(child, acc));
  return acc;
};

const findFirstTermNode = (node) => {
  if (node.type === 'term') {
    return node;
  }
  for (const child of node.children) {
    const found = findFirstTermNode(child);
    if (found) {
      return found;
    }
  }
  return null;
};

const findFirstNonEmptyTermNode = (node) => {
  if (node.type === 'term') {
    return String(node.value || '').trim() ? node : null;
  }
  for (const child of node.children) {
    const found = findFirstNonEmptyTermNode(child);
    if (found) {
      return found;
    }
  }
  return null;
};

const updateNodeById = (node, id, updater) => {
  if (node.id === id) {
    return updater(node);
  }
  if (node.type === 'group') {
    return { ...node, children: node.children.map((child) => updateNodeById(child, id, updater)) };
  }
  return node;
};

// Cascades: removing a node that empties its parent group removes that group too (recursively),
// and if the whole tree ends up empty, a fresh single-term root is returned so the form is never
// left with nothing to fill in.
const removeNodeById = (root, id) => {
  const remove = (node) => {
    if (node.type !== 'group') {
      return node;
    }
    const children = node.children
      .filter((child) => child.id !== id)
      .map((child) => remove(child))
      .filter((child) => child.type === 'term' || child.children.length > 0);
    return { ...node, children };
  };

  const result = remove(root);
  if (result.type === 'group' && result.children.length === 0) {
    return makeGroup([makeTerm()]);
  }
  return result;
};

const addChildToGroup = (root, groupId, newChild) => updateNodeById(root, groupId, (group) => ({
  ...group,
  children: [...group.children, newChild],
}));

const setFirstTermValue = (root, value) => {
  const firstTerm = findFirstTermNode(root);
  if (!firstTerm) {
    return makeGroup([makeTerm('title', value)]);
  }
  return updateNodeById(root, firstTerm.id, (node) => ({ ...node, value }));
};

const migrateLegacyTerms = (terms, operators) => makeGroup(
  (Array.isArray(terms) ? terms : []).map((term, index) => makeTerm(
    term.field || 'title',
    term.value || '',
    index === 0 ? 'AND' : (operators?.[index - 1] || 'AND'),
  )),
);

// Other entry points (e.g. PublicationTimeline.js deep-linking into a year-filtered search) write
// a plain expression tree to sessionStorage without `id`s, since those are an implementation
// detail of this component's React keys/lookups. Backfill them on the way in so the tree is
// always well-formed regardless of who wrote it.
const ensureNodeIds = (node) => {
  if (!node || typeof node !== 'object') {
    return node;
  }
  if (node.type === 'group') {
    return { ...node, id: node.id || nextNodeId(), children: (node.children || []).map(ensureNodeIds) };
  }
  return { ...node, id: node.id || nextNodeId() };
};

function AdvancedSearch() {
  const navigate = useNavigate();
  const [expression, setExpression] = useState(() => makeGroup([makeTerm()]));
  const [filters, setFilters] = useState({ yearFrom: '', yearTo: '', itemType: '', matchMode: 'contains' });
  const [itemTypes, setItemTypes] = useState([]);
  const [results, setResults] = useState([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 30;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');
  const [readingList, setReadingList] = useState([]);
  const [didYouMean, setDidYouMean] = useState('');
  const [autoCorrection, setAutoCorrection] = useState(null);
  const [suggestionsByNodeId, setSuggestionsByNodeId] = useState({});
  const [previewByNodeId, setPreviewByNodeId] = useState({});
  const [activeSuggestionNodeId, setActiveSuggestionNodeId] = useState(null);
  const [popularSearches, setPopularSearches] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const resultsRef = useRef(null);
  const firstResultRef = useRef(null);
  const pendingResultScrollRef = useRef(false);
  const suggestionTimerRef = useRef(null);
  const suggestionAbortRef = useRef(null);
  const suggestionsCacheRef = useRef(new Map());
  const previewAbortRef = useRef(null);
  const previewCacheRef = useRef(new Map());
  const advancedResultCacheRef = useRef(new Map());
  const requestSeqRef = useRef(0);

  const scrollResultIntoView = (target) => {
    if (!target) {
      return;
    }

    const header = document.querySelector('.institute-header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const extraGap = 12;
    const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - extraGap;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  useEffect(() => {
    const savedSearch = sessionStorage.getItem(ADVANCED_SEARCH_STORAGE_KEY);
    if (!savedSearch) {
      return;
    }

    try {
      const parsed = JSON.parse(savedSearch);
      let restoredExpression = null;
      if (parsed.expression) {
        restoredExpression = ensureNodeIds(parsed.expression);
        setExpression(restoredExpression);
      } else if (Array.isArray(parsed.terms) && parsed.terms.length) {
        restoredExpression = migrateLegacyTerms(parsed.terms, parsed.operators || []);
        setExpression(restoredExpression);
      }
      let restoredFilters = null;
      if (parsed.filters) {
        restoredFilters = {
          yearFrom: parsed.filters.yearFrom || '',
          yearTo: parsed.filters.yearTo || '',
          itemType: parsed.filters.itemType || '',
          matchMode: parsed.filters.matchMode || 'contains',
        };
        setFilters(restoredFilters);
      }
      if (typeof parsed.sortBy === 'string') {
        setSortBy(parsed.sortBy);
      }
      if (Array.isArray(parsed.results)) {
        setResults(parsed.results);
      }
      if (typeof parsed.offset === 'number') {
        setOffset(parsed.offset);
      }
      if (typeof parsed.total === 'number') {
        setTotal(parsed.total);
      }
      if (typeof parsed.error === 'string') {
        setError(parsed.error);
      }
      if (typeof parsed.didYouMean === 'string') {
        setDidYouMean(parsed.didYouMean);
      }
      if (parsed.autoSearch) {
        // Pass the freshly parsed values directly (not the `filters`/`expression` state, which
        // hasn't committed yet within this same effect) so the validation checks in runSearch
        // see real data instead of the pre-restore defaults.
        runSearch(0, parsed.sortBy || sortBy, restoredExpression || expression, {
          overrideFilters: restoredFilters || filters,
        });
      }

      if (typeof parsed.scrollY === 'number') {
        setTimeout(() => {
          window.scrollTo(0, parsed.scrollY);
        }, 100);
      }
    } catch (restoreError) {
      console.error('Failed to restore advanced search state', restoreError);
    }

    setReadingList(loadReadingList());

    try {
      const savedRecentSearches = JSON.parse(localStorage.getItem(ADVANCED_RECENT_SEARCHES_KEY) || '[]');
      if (Array.isArray(savedRecentSearches)) {
        setRecentSearches(savedRecentSearches.slice(0, 8));
      }
    } catch (_error) {
      // Ignore malformed payload.
    }

    return () => {
      if (suggestionTimerRef.current) {
        clearTimeout(suggestionTimerRef.current);
      }
      if (suggestionAbortRef.current) {
        suggestionAbortRef.current.abort();
      }
      if (previewAbortRef.current) {
        previewAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const fetchItemTypes = async () => {
      try {
        const response = await fetch('/api/books/counts');
        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const types = Object.keys(data || {})
          .filter((code) => code && code !== 'RC' && code !== 'C')
          .sort((a, b) => itemTypeLabel(a).localeCompare(itemTypeLabel(b), undefined, { sensitivity: 'base' }));
        setItemTypes(types);
      } catch (err) {
        console.error('Failed to fetch item types', err);
      }
    };

    fetchItemTypes();
  }, []);

  useEffect(() => {
    const primaryField = fieldToSuggestType(findFirstTermNode(expression)?.field || 'title');
    fetch(`/api/search/meta?type=${encodeURIComponent(primaryField)}&limit=8`)
      .then((response) => (response.ok ? response.json() : { popular: [] }))
      .then((data) => {
        setPopularSearches(Array.isArray(data.popular) ? data.popular : []);
      })
      .catch(() => {
        setPopularSearches([]);
      });
  }, [expression]);

  useEffect(() => {
    if (!pendingResultScrollRef.current || !results.length) {
      return;
    }

    pendingResultScrollRef.current = false;
    scrollResultIntoView(firstResultRef.current || resultsRef.current);
  }, [results, offset]);

  const highlightTokens = useMemo(
    () => flattenTerms(expression).flatMap((term) => tokenizeForHighlight(term.value)).slice(0, 12),
    [expression],
  );

  const persistSearchState = ({ nextExpression, nextFilters, nextSortBy, nextResults, nextOffset, nextTotal, nextError, nextDidYouMean = '' }) => {
    sessionStorage.setItem(ADVANCED_SEARCH_STORAGE_KEY, JSON.stringify({
      expression: nextExpression,
      filters: nextFilters,
      sortBy: nextSortBy,
      results: nextResults,
      offset: nextOffset,
      total: nextTotal,
      error: nextError,
      didYouMean: nextDidYouMean,
      scrollY: window.scrollY,
    }));
  };

  const persistRecentSearch = (query, field) => {
    const normalized = String(query || '').trim();
    if (!normalized) {
      return;
    }

    const nextRecent = [
      { query: normalized, field, at: new Date().toISOString() },
      ...recentSearches.filter((item) => !(item.query.toLowerCase() === normalized.toLowerCase() && item.field === field)),
    ].slice(0, 8);

    setRecentSearches(nextRecent);
    localStorage.setItem(ADVANCED_RECENT_SEARCHES_KEY, JSON.stringify(nextRecent));
  };

  const fetchSuggestions = async (nodeId, value, field) => {
    const trimmed = String(value || '').trim();
    if (trimmed.length < 2) {
      setSuggestionsByNodeId((prev) => ({ ...prev, [nodeId]: [] }));
      return;
    }

    const suggestionType = fieldToSuggestType(field);
    const cacheKey = `${suggestionType}::${trimmed.toLowerCase()}`;
    const cachedSuggestions = suggestionsCacheRef.current.get(cacheKey);
    if (cachedSuggestions) {
      setSuggestionsByNodeId((prev) => ({ ...prev, [nodeId]: cachedSuggestions }));
      return;
    }

    if (suggestionAbortRef.current) {
      suggestionAbortRef.current.abort();
    }

    const controller = new AbortController();
    suggestionAbortRef.current = controller;

    try {
      const response = await fetch(
        `/api/search/suggest?q=${encodeURIComponent(trimmed)}&type=${encodeURIComponent(suggestionType)}&limit=8`,
        { signal: controller.signal },
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const nextSuggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
      suggestionsCacheRef.current.set(cacheKey, nextSuggestions);
      setSuggestionsByNodeId((prev) => ({
        ...prev,
        [nodeId]: nextSuggestions,
      }));
    } catch (fetchError) {
      if (fetchError.name !== 'AbortError') {
        setSuggestionsByNodeId((prev) => ({ ...prev, [nodeId]: [] }));
      }
    }
  };

  // Mirrors SearchSection.js's live-preview dropdown so the two search experiences stay
  // consistent — mini result cards with covers, not just plain text suggestions.
  const fetchPreview = async (nodeId, value, field) => {
    const trimmed = String(value || '').trim();
    if (trimmed.length < 2) {
      setPreviewByNodeId((prev) => ({ ...prev, [nodeId]: [] }));
      return;
    }

    const previewType = fieldToSuggestType(field);
    const cacheKey = `${previewType}::${trimmed.toLowerCase()}`;
    const cachedPreview = previewCacheRef.current.get(cacheKey);
    if (cachedPreview) {
      setPreviewByNodeId((prev) => ({ ...prev, [nodeId]: cachedPreview }));
      return;
    }

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }

    const controller = new AbortController();
    previewAbortRef.current = controller;

    try {
      const response = await fetch(
        `/api/search/preview?q=${encodeURIComponent(trimmed)}&type=${encodeURIComponent(previewType)}&limit=5`,
        { signal: controller.signal },
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const nextPreview = Array.isArray(data.books) ? data.books : [];
      previewCacheRef.current.set(cacheKey, nextPreview);
      setPreviewByNodeId((prev) => ({ ...prev, [nodeId]: nextPreview }));
    } catch (fetchError) {
      if (fetchError.name !== 'AbortError') {
        setPreviewByNodeId((prev) => ({ ...prev, [nodeId]: [] }));
      }
    }
  };

  const fetchDidYouMean = async (payloadTerms) => {
    const firstTerm = payloadTerms.find((term) => term.value)?.value || '';
    const firstField = payloadTerms.find((term) => term.value)?.field || 'title';

    if (!firstTerm || firstTerm.length < 3) {
      return { suggestion: '', autoApplied: false };
    }

    const apiType = firstField === 'accessionNumber' ? 'bc' : firstField;
    if (!['title', 'author', 'subject', 'isbn', 'bc'].includes(apiType)) {
      return { suggestion: '', autoApplied: false };
    }

    try {
      const response = await fetch('/api/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: firstTerm, type: apiType, limit: 1, offset: 0 }),
      });

      if (!response.ok) {
        return { suggestion: '', autoApplied: false };
      }

      const data = await response.json();
      return {
        suggestion: String(data.didYouMean || '').trim(),
        autoApplied: Boolean(data.autoApplied && data.didYouMean),
      };
    } catch (_error) {
      return { suggestion: '', autoApplied: false };
    }
  };

  const runSearch = async (nextOffset = 0, nextSortBy = sortBy, sourceExpression = expression, { skipAutoCorrect = false, overrideFilters = null } = {}) => {
    // `overrideFilters` lets a caller (e.g. the restore-from-sessionStorage effect) pass freshly
    // parsed filter values directly instead of reading the `filters` state closure, which may
    // not have committed yet if this is called in the same tick as a `setFilters` call.
    const effectiveFilters = overrideFilters || filters;
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    const sourceTerms = flattenTerms(sourceExpression);
    const hasTerms = sourceTerms.length > 0;

    if (!hasTerms && !effectiveFilters.yearFrom && !effectiveFilters.yearTo && !effectiveFilters.itemType) {
      setError('Add at least one search term or filter.');
      return;
    }

    if (effectiveFilters.yearFrom && !/^\d{4}$/.test(effectiveFilters.yearFrom)) {
      setError('Year From must be a 4-digit year.');
      return;
    }

    if (effectiveFilters.yearTo && !/^\d{4}$/.test(effectiveFilters.yearTo)) {
      setError('Year To must be a 4-digit year.');
      return;
    }

    if (effectiveFilters.yearFrom && effectiveFilters.yearTo && effectiveFilters.yearFrom > effectiveFilters.yearTo) {
      setError('Year From cannot be greater than Year To.');
      return;
    }

    setLoading(true);
    setError(null);
    setDidYouMean('');
    if (!skipAutoCorrect) {
      setAutoCorrection(null);
    }

    const payload = {
      expression: sourceExpression,
      itemType: effectiveFilters.itemType,
      yearFrom: effectiveFilters.yearFrom,
      yearTo: effectiveFilters.yearTo,
      matchMode: effectiveFilters.matchMode,
      sortBy: nextSortBy,
      limit: pageSize,
      offset: nextOffset,
    };

    const cacheKey = JSON.stringify(payload);
    const cached = advancedResultCacheRef.current.get(cacheKey);
    if (cached) {
      if (requestSeq !== requestSeqRef.current) {
        return;
      }
      const nextResults = cached.books || [];
      const nextTotal = cached.total || 0;
      setResults(nextResults);
      setTotal(nextTotal);
      setOffset(nextOffset);

      let nextError = null;
      if (nextResults.length === 0) {
        nextError = 'No results found for selected criteria.';
        setError(nextError);
      }

      persistSearchState({
        nextExpression: sourceExpression,
        nextFilters: effectiveFilters,
        nextSortBy,
        nextResults,
        nextOffset,
        nextTotal,
        nextError,
        nextDidYouMean: '',
      });
      setLoading(false);
      return;
    }

    try {
      if (nextOffset === 0) {
        const combinedQuery = sourceTerms.map((term) => term.value).join(' ').trim();
        if (combinedQuery) {
          trackEvent('search_submitted', {
            query: combinedQuery.slice(0, 120),
            queryLength: combinedQuery.length,
            searchType: 'advanced',
          });

          const firstTerm = sourceTerms[0];
          if (firstTerm) {
            persistRecentSearch(firstTerm.value, firstTerm.field);
          }
        }
      }

      const response = await fetch('/api/search/advanced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const responseBody = await response.json().catch(() => ({}));
        throw new Error(responseBody.error || 'Advanced search failed');
      }

      const data = await response.json();
      if (requestSeq !== requestSeqRef.current) {
        return;
      }
      const nextResults = data.books || [];
      const nextTotal = data.total || 0;

      advancedResultCacheRef.current.set(cacheKey, { books: nextResults, total: nextTotal });
      if (advancedResultCacheRef.current.size > 120) {
        const oldestKey = advancedResultCacheRef.current.keys().next().value;
        if (oldestKey) {
          advancedResultCacheRef.current.delete(oldestKey);
        }
      }

      setResults(nextResults);
      setTotal(nextTotal);
      setOffset(nextOffset);

      let nextError = null;
      let nextDidYouMean = '';
      if (nextResults.length === 0) {
        if (nextOffset === 0) {
          trackEvent('search_zero_results', {
            searchType: 'advanced',
            query: sourceTerms.map((term) => term.value).join(' ').slice(0, 120),
            queryLength: sourceTerms.map((term) => term.value).join(' ').length,
          });
        }

        const correction = !skipAutoCorrect ? await fetchDidYouMean(sourceTerms) : { suggestion: '', autoApplied: false };

        if (correction.autoApplied && correction.suggestion) {
          const firstNonEmptyTerm = findFirstNonEmptyTermNode(sourceExpression);
          if (firstNonEmptyTerm) {
            const originalValue = firstNonEmptyTerm.value.trim();
            const correctedExpression = updateNodeById(sourceExpression, firstNonEmptyTerm.id, (node) => ({
              ...node,
              value: correction.suggestion,
            }));
            setExpression(correctedExpression);
            setAutoCorrection({ from: originalValue, to: correction.suggestion });
            trackEvent('search_autocorrected', {
              from: originalValue.slice(0, 120),
              to: correction.suggestion.slice(0, 120),
              searchType: 'advanced',
            });
            await runSearch(0, nextSortBy, correctedExpression, { skipAutoCorrect: true });
            return;
          }
        }

        nextError = 'No results found for selected criteria.';
        setError(nextError);
        nextDidYouMean = correction.suggestion;
        setDidYouMean(nextDidYouMean);
      }

      persistSearchState({
        nextExpression: sourceExpression,
        nextFilters: effectiveFilters,
        nextSortBy,
        nextResults,
        nextOffset,
        nextTotal,
        nextError,
        nextDidYouMean,
      });
    } catch (err) {
      if (requestSeq !== requestSeqRef.current) {
        return;
      }
      setError(err.message || 'Failed to run advanced search.');
      console.error(err);
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    runSearch(0);
  };

  const handleClear = () => {
    setExpression(makeGroup([makeTerm()]));
    setFilters({ yearFrom: '', yearTo: '', itemType: '', matchMode: 'contains' });
    setResults([]);
    setOffset(0);
    setTotal(0);
    setError(null);
    setDidYouMean('');
    setAutoCorrection(null);
    setSuggestionsByNodeId({});
    setPreviewByNodeId({});
    suggestionsCacheRef.current.clear();
    previewCacheRef.current.clear();
    advancedResultCacheRef.current.clear();
    sessionStorage.removeItem(ADVANCED_SEARCH_STORAGE_KEY);
  };

  const handleTermValueChange = (nodeId, value, field) => {
    setExpression((prev) => updateNodeById(prev, nodeId, (node) => ({ ...node, value })));
    setDidYouMean('');
    setActiveSuggestionNodeId(nodeId);

    if (suggestionTimerRef.current) {
      clearTimeout(suggestionTimerRef.current);
    }

    suggestionTimerRef.current = setTimeout(() => {
      fetchSuggestions(nodeId, value, field);
      fetchPreview(nodeId, value, field);
    }, 180);
  };

  const handleUpdateTermField = (nodeId, field) => {
    setExpression((prev) => updateNodeById(prev, nodeId, (node) => ({ ...node, field })));
    setSuggestionsByNodeId((prev) => ({ ...prev, [nodeId]: [] }));
    setPreviewByNodeId((prev) => ({ ...prev, [nodeId]: [] }));
  };

  const handleUpdateOperator = (nodeId, operator) => {
    setExpression((prev) => updateNodeById(prev, nodeId, (node) => ({ ...node, operator })));
  };

  const handleAddTerm = (groupId) => {
    if (countTerms(expression) >= MAX_TERMS) {
      return;
    }
    setExpression((prev) => addChildToGroup(prev, groupId, makeTerm('title', '', 'AND')));
  };

  const handleAddGroup = (groupId) => {
    if (countTerms(expression) >= MAX_TERMS) {
      return;
    }
    setExpression((prev) => addChildToGroup(prev, groupId, makeGroup([makeTerm('title', '', 'AND')], 'AND')));
  };

  const handleRemoveNode = (nodeId) => {
    setExpression((prev) => removeNodeById(prev, nodeId));
    setSuggestionsByNodeId((prev) => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
    setPreviewByNodeId((prev) => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
  };

  const renderHighlightedText = (text) => {
    const value = String(text || '');
    if (!value || !highlightTokens.length) {
      return value;
    }

    const pattern = highlightTokens.map((token) => escapeRegExp(token)).join('|');
    const regex = new RegExp(`(${pattern})`, 'ig');
    const parts = value.split(regex);

    return parts.map((part, index) => {
      const normalized = part.toLowerCase();
      if (highlightTokens.includes(normalized)) {
        return <mark className="result-mark" key={`h-${index}`}>{part}</mark>;
      }
      return <span key={`t-${index}`}>{part}</span>;
    });
  };

  const runSuggestedSearch = (queryText, source = 'suggestion') => {
    const nextExpression = setFirstTermValue(expression, queryText);

    setExpression(nextExpression);
    setDidYouMean('');
    setSuggestionsByNodeId({});
    setActiveSuggestionNodeId(null);

    if (source === 'suggestion') {
      trackEvent('search_suggestion_clicked', {
        query: queryText.slice(0, 120),
        searchType: 'advanced',
      });
    }

    runSearch(0, sortBy, nextExpression);
  };

  const openBook = (book, index) => {
    trackEvent('search_result_clicked', {
      searchType: 'advanced',
      rank: offset + index + 1,
      query: flattenTerms(expression).map((term) => term.value).join(' ').slice(0, 120),
      biblioId: book.biblio_id,
    });

    trackEvent('book_detail_opened', {
      from: 'advanced-search',
      biblioId: book.biblio_id,
    });

    persistSearchState({
      nextExpression: expression,
      nextFilters: filters,
      nextSortBy: sortBy,
      nextResults: results,
      nextOffset: offset,
      nextTotal: total,
      nextError: error,
      nextDidYouMean: didYouMean,
    });
    navigate(`/book/${book.biblio_id}`, { state: { from: 'advanced-search' } });
  };

  const toggleReadingList = (book) => {
    if (isInReadingList(book.biblio_id)) {
      setReadingList(removeFromReadingList(book.biblio_id));
      return;
    }

    setReadingList(addToReadingList(book));
  };

  const handleExportRis = () => {
    if (!readingList.length) {
      return;
    }
    const content = generateRis(readingList);
    downloadTextFile('opac-reading-list.ris', content, 'application/x-research-info-systems');
  };

  const handleExportBibtex = () => {
    if (!readingList.length) {
      return;
    }
    const content = generateBibtex(readingList);
    downloadTextFile('opac-reading-list.bib', content, 'application/x-bibtex');
  };

  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canGoBack = offset > 0;
  const canGoNext = offset + pageSize < total;
  const lastPageOffset = Math.max(0, (totalPages - 1) * pageSize);
  const hasResults = results.length > 0;
  const showPagination = hasResults && totalPages > 1;
  const termCount = countTerms(expression);

  const renderTermInput = (node) => {
    const preview = previewByNodeId[node.id] || [];
    const suggestions = suggestionsByNodeId[node.id] || [];
    const isOpen = activeSuggestionNodeId === node.id && (preview.length > 0 || suggestions.length > 0);

    return (
      <div className="term-input-wrap">
        <input
          value={node.value}
          onChange={(e) => handleTermValueChange(node.id, e.target.value, node.field)}
          onFocus={() => setActiveSuggestionNodeId(node.id)}
          onBlur={() => setTimeout(() => setActiveSuggestionNodeId(null), 120)}
          placeholder="Enter keyword"
          autoComplete="off"
        />
        {isOpen && (
          <div className="term-suggestions" role="listbox" aria-label="Advanced search suggestions">
            {preview.length > 0 && (
              <div className="search-preview-list">
                {preview.map((book) => (
                  <button
                    key={book.biblio_id}
                    type="button"
                    className="search-preview-item"
                    onMouseDown={() => {
                      trackEvent('search_preview_clicked', {
                        searchType: 'advanced',
                        biblioId: book.biblio_id,
                      });
                      navigate(`/book/${book.biblio_id}`, { state: { from: 'advanced-search' } });
                    }}
                  >
                    <CoverImage
                      biblioId={book.biblio_id}
                      isbn={book.isbn}
                      alt={book.title}
                      className="search-preview-item__cover"
                      width={36}
                      height={52}
                      sizeCode="S"
                    />
                    <span className="search-preview-item__info">
                      <span className="search-preview-item__title">{book.title}</span>
                      {book.author && <span className="search-preview-item__author">{book.author}</span>}
                      <span className="search-preview-item__avail">
                        {Number(book.available_count || 0) > 0
                          ? `${book.available_count} available`
                          : 'Unavailable'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {suggestions.length > 0 && (
              <div className="search-suggestion-list">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="term-suggestion-item"
                    onMouseDown={() => {
                      setExpression((prev) => updateNodeById(prev, node.id, (n) => ({ ...n, value: suggestion })));
                      setSuggestionsByNodeId((prev) => ({ ...prev, [node.id]: [] }));
                      setActiveSuggestionNodeId(null);
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (groupNode, isRoot = false) => (
    <div className={isRoot ? 'boolean-builder' : 'boolean-group'} data-group-id={groupNode.id}>
      {groupNode.children.map((child, index) => (
        <div className="boolean-row" key={child.id}>
          {index > 0 && (
            <select
              className="operator-select"
              value={child.operator || 'AND'}
              onChange={(e) => handleUpdateOperator(child.id, e.target.value)}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
              <option value="NOT">NOT</option>
            </select>
          )}

          {child.type === 'term' ? (
            <>
              <select
                className="field-select"
                value={child.field}
                onChange={(e) => handleUpdateTermField(child.id, e.target.value)}
              >
                <option value="title">Title</option>
                <option value="author">Author</option>
                <option value="subject">Subject</option>
                <option value="isbn">ISBN</option>
                <option value="callnumber">Call Number</option>
                <option value="accessionNumber">Accession Number</option>
              </select>

              {renderTermInput(child)}

              <button
                type="button"
                className="icon-btn"
                onClick={() => handleRemoveNode(child.id)}
                disabled={termCount <= 1}
                title="Remove term"
              >
                −
              </button>
            </>
          ) : (
            <div className="boolean-group-wrap">
              {renderGroup(child)}
              <button
                type="button"
                className="icon-btn group-remove-btn"
                onClick={() => handleRemoveNode(child.id)}
                title="Remove group"
              >
                Remove group
              </button>
            </div>
          )}
        </div>
      ))}

      <div className="boolean-group-actions">
        <button type="button" className="add-term-btn" onClick={() => handleAddTerm(groupNode.id)} disabled={termCount >= MAX_TERMS}>
          + Add term
        </button>
        <button type="button" className="add-term-btn add-group-btn" onClick={() => handleAddGroup(groupNode.id)} disabled={termCount >= MAX_TERMS}>
          + Add group ( )
        </button>
      </div>
    </div>
  );

  return (
    <section className="advanced-search-page">
      <div className="advanced-search-header theme-surface">
        <div>
          <h2>Advanced Search</h2>
          <p>Combine terms with Boolean operators — including parenthetical groups — and refine by item type and year.</p>
        </div>
        <div className="header-actions">
          <button type="button" className="home-btn" onClick={() => navigate('/')}>Home</button>
        </div>
      </div>

      <form className="advanced-search-form theme-surface" onSubmit={handleSearch}>
        {renderGroup(expression, true)}

        <div className="advanced-search-pills" aria-label="Recent and popular advanced search suggestions">
          {recentSearches.slice(0, 4).map((item) => (
            <button
              key={`recent-${item.field}-${item.query}`}
              type="button"
              className="advanced-search-pill recent"
              onClick={() => runSuggestedSearch(item.query, 'recent')}
            >
              Recent: {item.query}
            </button>
          ))}
          {popularSearches.slice(0, 4).map((item) => (
            <button
              key={`popular-${item.query}`}
              type="button"
              className="advanced-search-pill popular"
              onClick={() => runSuggestedSearch(item.query, 'popular')}
            >
              Popular: {item.query}
            </button>
          ))}
        </div>

        <div className="grid">
          <select value={filters.matchMode} onChange={(e) => setFilters((prev) => ({ ...prev, matchMode: e.target.value }))}>
            <option value="contains">Contains</option>
            <option value="exact">Exact match</option>
          </select>
          <select value={filters.itemType} onChange={(e) => setFilters((prev) => ({ ...prev, itemType: e.target.value }))}>
            <option value="">All Item Types</option>
            {itemTypes.map((type) => (
              <option key={type} value={type}>{itemTypeLabel(type)}</option>
            ))}
          </select>
          <input
            placeholder="Year From (YYYY)"
            value={filters.yearFrom}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 4);
              setFilters((prev) => ({ ...prev, yearFrom: digitsOnly }));
            }}
            maxLength={4}
            inputMode="numeric"
          />
          <input
            placeholder="Year To (YYYY)"
            value={filters.yearTo}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 4);
              setFilters((prev) => ({ ...prev, yearTo: digitsOnly }));
            }}
            maxLength={4}
            inputMode="numeric"
          />
        </div>

        <div className="actions">
          <button className="search-btn" type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
          <button className="clear-btn" type="button" onClick={handleClear}>Clear</button>
        </div>
      </form>

      {error && !autoCorrection && <div className="error">{error}</div>}

      {autoCorrection && (
        <div className="advanced-did-you-mean advanced-did-you-mean--auto">
          Showing results for <strong>{autoCorrection.to}</strong> instead of "{autoCorrection.from}".{' '}
          <button type="button" onClick={() => runSuggestedSearch(autoCorrection.from, 'auto-correct-revert')}>
            Search instead for "{autoCorrection.from}"
          </button>
        </div>
      )}

      {!autoCorrection && didYouMean && (
        <div className="advanced-did-you-mean">
          Did you mean{' '}
          <button type="button" onClick={() => runSuggestedSearch(didYouMean, 'did-you-mean')}>
            {didYouMean}
          </button>
          ?
        </div>
      )}

      {hasResults && (
        <div className="results theme-surface" ref={resultsRef}>
          <div className="results-head">
            <h3>Results ({total})</h3>
            <div className="results-controls">
              <label className="sort-label" htmlFor="advanced-sort">Sort by:</label>
              <select
                id="advanced-sort"
                className="sort-select"
                value={sortBy}
                onChange={(e) => {
                  const nextSortBy = e.target.value;
                  setSortBy(nextSortBy);
                  setOffset(0);
                  pendingResultScrollRef.current = true;
                  setTimeout(() => runSearch(0, nextSortBy), 0);
                }}
              >
                <option value="relevance">Relevance</option>
                <option value="availability">Availability</option>
                <option value="newest">Newest</option>
              </select>
              <span>Page {currentPage} of {totalPages}</span>
            </div>
          </div>
          <div className="reading-list-toolbar advanced-reading-toolbar">
            <span>Reading List: {readingList.length}</span>
            <div className="reading-list-actions">
              <button type="button" className="reading-export-btn" onClick={handleExportRis} disabled={!readingList.length}>
                Export RIS
              </button>
              <button type="button" className="reading-export-btn" onClick={handleExportBibtex} disabled={!readingList.length}>
                Export BibTeX
              </button>
              <button type="button" className="reading-clear-btn" onClick={() => setReadingList(clearReadingList())} disabled={!readingList.length}>
                Clear List
              </button>
            </div>
          </div>
          <div className="result-list">
            {results.map((book, index) => (
              <div
                key={book.biblio_id}
                className="result-card"
                ref={index === 0 ? firstResultRef : undefined}
              >
                <div className="result-cover-wrap">
                  <CoverImage
                    biblioId={book.biblio_id}
                    isbn={book.isbn}
                    alt={book.title}
                    className="result-cover"
                    width={96}
                    height={140}
                    sizeCode="M"
                  />
                </div>
                <div className="result-content">
                  <h4>{renderHighlightedText(book.title)}</h4>
                  {book.author && <p className="author">by {renderHighlightedText(book.author)}</p>}
                  {book.isbn && <p className="isbn">ISBN: {book.isbn}</p>}
                  <p className="item-count">📚 {book.item_count} item(s), {Number(book.available_count || 0)} available</p>
                  <button onClick={() => openBook(book, index)} className="details-btn">View Details</button>
                  <button type="button" className="reading-toggle-btn" onClick={() => toggleReadingList(book)}>
                    {isInReadingList(book.biblio_id) ? 'Remove from Reading List' : 'Add to Reading List'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          {showPagination && (
            <div className="actions pagination-row">
              <div className="pagination-group left">
                <button
                  className="pagination-btn"
                  type="button"
                  onClick={() => {
                    pendingResultScrollRef.current = true;
                    runSearch(0);
                  }}
                  disabled={loading || !canGoBack}
                >
                  « First
                </button>
                <button
                  className="pagination-btn"
                  type="button"
                  onClick={() => {
                    pendingResultScrollRef.current = true;
                    runSearch(Math.max(0, offset - pageSize));
                  }}
                  disabled={loading || !canGoBack}
                >
                  ← Previous
                </button>
              </div>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <div className="pagination-group right">
                <button
                  className="pagination-btn primary"
                  type="button"
                  onClick={() => {
                    pendingResultScrollRef.current = true;
                    runSearch(offset + pageSize);
                  }}
                  disabled={loading || !canGoNext}
                >
                  Next →
                </button>
                <button
                  className="pagination-btn primary"
                  type="button"
                  onClick={() => {
                    pendingResultScrollRef.current = true;
                    runSearch(lastPageOffset);
                  }}
                  disabled={loading || !canGoNext}
                >
                  Last »
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default AdvancedSearch;
